import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteEncounter, getEncounterById, saveEncounter, searchEncounters} from "@/api/EncounterApi.ts";
import type Encounter from "@/encounter/model/Encounter.ts";

export const encounterKeys = {
    all: ["encounters"] as const,
    lists: () => [...encounterKeys.all, "list"] as const,
    list: (campaignId: string, name: string, pageSize: number) =>
        [...encounterKeys.lists(), campaignId, name, pageSize] as const,
    detail: (id: string) => [...encounterKeys.all, "detail", id] as const,
};

export function useEncounterQuery(id?: string) {
    return useQuery({
        queryKey: encounterKeys.detail(id ?? ""),
        queryFn: ({signal}) => getEncounterById(id!, signal),
        enabled: Boolean(id),
    });
}

export function useEncountersQuery(campaignId: string, name: string, pageSize = 30) {
    const query = useInfiniteQuery({
        queryKey: encounterKeys.list(campaignId, name, pageSize),
        initialPageParam: 0,
        queryFn: ({pageParam}) => searchEncounters({page: pageParam, size: pageSize}, {campaignId, name}),
        getNextPageParam: page => {
            const nextPage = page.page.number + 1;
            return nextPage < page.page.totalPages ? nextPage : undefined;
        },
    });
    return {
        ...query,
        encounters: query.data?.pages.flatMap(page => page.content) ?? [],
        totalCount: query.data?.pages[0]?.page.totalElements ?? 0,
        loadMore: async () => {
            if (query.hasNextPage) await query.fetchNextPage({cancelRefetch: false});
        },
    };
}

export function useSaveEncounterMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: saveEncounter,
        onSuccess: (_, encounter: Encounter) => {
            void queryClient.invalidateQueries({queryKey: encounterKeys.detail(encounter.id)});
            void queryClient.invalidateQueries({queryKey: encounterKeys.lists()});
        },
    });
}

export function useDeleteEncounterMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteEncounter,
        onSuccess: (_, id) => {
            queryClient.removeQueries({queryKey: encounterKeys.detail(id)});
            void queryClient.invalidateQueries({queryKey: encounterKeys.lists()});
        },
    });
}
